import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

import { layout, media, remCalc } from '@prisjakt/ui-base';
import RatingComponent from 'src/components/rating';

import {
  evenColumnBackgroundColor,
  oddColumnBackgroundColor,
  dummyMaxWidth,
  imageWidthVal,
  imageWidthMobileVal,
  mobileItemMaxWidth,
  tabletItemMaxWidth,
  desktopItemMaxWidth,
  highlightNewEvenColumnAnimation,
  highlightNewOddColumnAnimation,
} from '../../../../constants';

const headerMinWidthDesktopVal = 216;
const headerMinWidthTabletVal = 180;
const headerMinWidthMobileVal = 140;
const headerPriceFontSize = remCalc(16);
const headerTitleFontSize = remCalc(15);
const titleMinHeightVal = 65;
const ratingMinHeightVal = 20;

const headerStyling = css`
  padding: ${layout.measurements.spacing050};
  padding-top: ${layout.measurements.spacing100};
  min-width: ${headerMinWidthDesktopVal}px;

  ${media.mediumDown`
    min-width: ${headerMinWidthTabletVal}px;
  `}

  ${media.smallOnly`
    min-width: ${headerMinWidthMobileVal}px;
  `};
`;

const backgroundOverlay = css`
  position: absolute;
  content: '';
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

type HeaderProps = {
  desktopScaleToFit?: boolean;
  shouldAnimate?: boolean;
  hasScrollSnap?: boolean;
};
export const Header = styled.div<HeaderProps>(
  props => css`
  ${headerStyling};
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1 1 0%;

  ${props.hasScrollSnap &&
    css`
      scroll-snap-align: start;
    `}

  &:nth-child(odd)::after {
    ${backgroundOverlay};
    background-color: ${oddColumnBackgroundColor};
    ${props.shouldAnimate && highlightNewOddColumnAnimation}
  }

  &:nth-child(even)::after {
    ${backgroundOverlay};
    background-color: ${evenColumnBackgroundColor};
    ${props.shouldAnimate && highlightNewEvenColumnAnimation}
  }

  ${media.smallOnly`
    width: ${mobileItemMaxWidth};
  `}

  ${media.mediumOnly`
    width: ${tabletItemMaxWidth};
  `}

  ${media.largeUp`
    width: ${props.desktopScaleToFit && desktopItemMaxWidth};
  `}
`
);

type DummyHeaderProps = {
  hasMaxWidth?: boolean;
};

export const DummyHeader = styled.div<DummyHeaderProps>`
  background-color: ${evenColumnBackgroundColor};
  ${headerStyling};
  flex: 1 1 0%;

  ${p =>
    p.hasMaxWidth &&
    css`
      max-width: ${dummyMaxWidth}px;
    `};

  ${media.smallOnly`
    max-width: 100%;
  `};
`;

export const Image = styled(Link)`
  width: ${imageWidthVal}px;
  height: ${imageWidthVal}px;
  margin-bottom: ${layout.measurements.spacing050};

  ${media.smallOnly`
  width: ${imageWidthMobileVal}px;
  height: ${imageWidthMobileVal}px;
  `}
`;

export const Title = styled.h2`
  font-weight: 600;
  text-align: center;
  font-size: ${headerTitleFontSize};
  height: ${titleMinHeightVal}px;
  margin-bottom: ${layout.measurements.spacing050};
  overflow: hidden;
`;

export const Price = styled.h3`
  color: ${p => p.theme.colors.negative700};
  font-weight: 600;
  text-align: center;
  font-size: ${headerPriceFontSize};
  margin-bottom: 0;
`;

type WrapperProps = {
  hide: boolean;
};

export const SentimentBadge = styled.div<WrapperProps>`
  min-height: 46px;
  margin-bottom: ${layout.measurements.spacing025};

  ${props =>
    props.hide &&
    css`
      display: none;
    `}
`;

export const ShippingPrice = styled.div`
  color: ${p => p.theme.text.quaternary};
  margin-bottom: ${layout.measurements.spacing050};
`;

export const RatingWrapper = styled.div<WrapperProps>`
  min-height: ${ratingMinHeightVal}px;
  width: 100%;
  margin-bottom: ${layout.measurements.spacing050};

  ${props =>
    props.hide &&
    css`
      display: none;
    `}
`;

export const Rating = styled(RatingComponent)`
  justify-content: center;
`;

export const CloseButton = styled(Link)`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 24px;
  height: 24px;
  color: ${p => p.theme.icons.tertiary};
`;
